import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/pro-light-svg-icons";
import { useV2NotificationsUnreadCountQuery } from "@generated";
import useFlags from "@common/hooks/useFlags";
import NotificationDropdown from "@components/NotificationDropdown";
import { SIDEBAR_EXPANDED_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from "./Sidebar";

interface NotificationBellV2Props {
  expanded: boolean;
}

const NotificationBellV2: React.FC<NotificationBellV2Props> = ({
  expanded,
}) => {
  const flags = useFlags();
  const enabled = Boolean(flags.releaseNotificationManagement);
  const [open, setOpen] = useState(false);
  const [panelPosition, setPanelPosition] =
    useState<{
      top: number;
      left: number;
    } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { data, refetch } = useV2NotificationsUnreadCountQuery(undefined, {
    skip: !enabled,
  });

  const toggleOpen = () => {
    if (!open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      // Pinned to the sidebar's own current width (not measured from the
      // container's own edge) so the panel always sits flush against the
      // sidebar's right edge, whether it's expanded or collapsed
      setPanelPosition({
        top: rect.top,
        left: expanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
      });
    }
    setOpen((prev) => !prev);
  };

  // The panel is portaled to <body> (see below), so it's not a DOM
  // descendant of containerRef — check both refs before treating a click as
  // "outside."
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!enabled) return null;

  const unreadCount = data?.["unread-count"] ?? 0;
  const hasUnread = unreadCount > 0;
  const badgeLabel = unreadCount > 9 ? "9+" : String(unreadCount);
  const ariaLabel = hasUnread
    ? `Notifications, ${unreadCount} unread`
    : "Notifications";

  const button = (
    <button
      type="button"
      id="nav-notifications"
      aria-label={ariaLabel}
      onClick={toggleOpen}
      style={{
        color: "rgba(255,255,255,0.7)",
        backgroundColor: "transparent",
        border: "none",
        boxSizing: "border-box",
        width: "calc(100% - 16px)",
        padding: expanded ? "10px 16px" : "10px 0",
        display: "flex",
        alignItems: "center",
        justifyContent: expanded ? "flex-start" : "center",
        gap: expanded ? 12 : 0,
        borderRadius: 4,
        margin: "2px 8px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        fontSize: 14,
        fontFamily: "Open Sans, sans-serif",
        transition: "background-color 0.15s ease",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor =
          "rgba(255,255,255,0.05)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
      }}
    >
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          flexShrink: 0,
        }}
      >
        <FontAwesomeIcon
          icon={faBell}
          style={{
            fontSize: expanded ? 16 : 20,
            width: expanded ? 16 : 20,
          }}
        />
        {hasUnread && (
          <span
            data-testid="notification-badge"
            aria-hidden="true"
            style={{
              position: "absolute",
              top: -6,
              right: -8,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              boxSizing: "border-box",
              borderRadius: 8,
              backgroundColor: "#DC3545",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: "16px",
              textAlign: "center",
            }}
          >
            {badgeLabel}
          </span>
        )}
      </span>
      {expanded && <span>Notifications</span>}
    </button>
  );

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {expanded ? (
        button
      ) : (
        <OverlayTrigger
          placement="right"
          overlay={
            <Tooltip id="tooltip-nav-notifications">Notifications</Tooltip>
          }
        >
          {button}
        </OverlayTrigger>
      )}
      {open &&
        panelPosition &&
        // The type mismatch here (Element vs ReactNode) is a
        // @types/react-dom typing quirk with createPortal + forwardRef
        // components, not a real bug — createPortal accepts any renderable
        // node at runtime.
        createPortal(
          (
            <NotificationDropdown
              ref={panelRef}
              refetchUnreadCount={refetch}
              style={{
                position: "fixed",
                top: panelPosition.top,
                left: panelPosition.left,
                marginTop: 0,
              }}
            />
          ) as React.ReactNode as Parameters<typeof createPortal>[0],
          document.body
        )}
    </div>
  );
};

export default NotificationBellV2;
